import React, { useState } from 'react';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, ColumnApi, ICellRendererParams } from 'ag-grid-enterprise';
import { AppState } from '$/store';
import { getIsCurrentUserSuperAdminState } from '$/store/profile/selectors';
import { generatePrimer, generateTearSheet } from '$/store/company/actions';
import NoDataFound from '$/components/global/noData';
import * as Bixby from '$/core';
import './index.scss';
import './CompanyDocuments.scss';
import { getIsCurrentCompanyPublicState } from '$/store/company/selectors';

export interface ICompanyProfileProps {
  currentCompany?: Bixby.Models.AdminCompany;
  isSuperAdmin: boolean;
  isCurrentCompanyPublic: boolean;
  dispatchGeneratePrimer: (companyId: string, clientId: string | null) => void;
  dispatchGenerateTearSheet: (companyId: string, clientId: string | null) => void;
}

const CompanyDocuments: React.FC<ICompanyProfileProps> = props => {
  const { currentCompany, isSuperAdmin, isCurrentCompanyPublic, dispatchGeneratePrimer, dispatchGenerateTearSheet } =
    props;
  const [, setDocumentGridApi] = useState(null as GridApi | null);
  const [, setDocumentGridColumnApi] = useState(null as ColumnApi | null);
  const [, setPrimerGridApi] = useState(null as GridApi | null);
  const [, setPrimerGridColumnApi] = useState(null as ColumnApi | null);
  const [, setTearSheetGridApi] = useState(null as GridApi | null);
  const [, setTearSheetGridColumnApi] = useState(null as ColumnApi | null);

  const formattedDate = (dateString: string) => {
    const dayjsDate: dayjs.Dayjs | null = dateString ? dayjs.utc(dateString) : null;
    return dayjsDate && dayjsDate.isValid() ? Bixby.Utils.formatDateTime(dayjsDate) : null;
  };

  const bixbyDocuments: Bixby.Models.BixbyDocument[] = currentCompany
    ? [
        {
          document: 'Tearsheet',
          updatedDate: formattedDate(currentCompany.tearSheetUpdatedDate),
          updatedBy: currentCompany.tearSheetUpdatedBy,
          state: currentCompany.tearSheetState,
          link: `${currentCompany.documentLibraryWebUrl}/${Bixby.Constants.GENERATED_DOCUMENTS}/${Bixby.Constants.GENERATED_DOCUMENTS_BIXBY}/${Bixby.Constants.TEARSHEET}`,
          linkText: Bixby.Constants.TEARSHEET,
        },
        {
          document: 'Primer',
          updatedDate: formattedDate(currentCompany.primerUpdatedDate),
          updatedBy: currentCompany.primerUpdatedBy,
          state: currentCompany.primerState,
          link: `${currentCompany.documentLibraryWebUrl}/${Bixby.Constants.GENERATED_DOCUMENTS}/${Bixby.Constants.GENERATED_DOCUMENTS_BIXBY}/${Bixby.Constants.PRIMER}`,
          linkText: Bixby.Constants.PRIMER,
        },
      ]
    : [];

  const isBixbyDocumentGenerateDisabled = (row: Bixby.Models.BixbyDocument) => {
    if (currentCompany && currentCompany.declarationType !== Bixby.Enums.DeclarationType.Private) {
      switch (row.document) {
        case 'Tearsheet':
          return currentCompany.tearSheetState == Bixby.Enums.DocumentState.Pending;
        case 'Primer':
          return currentCompany.primerState == Bixby.Enums.DocumentState.Pending;
      }
    }

    return false;
  };

  const bixbyDocumentGenerate = (row: Bixby.Models.BixbyDocument) => {
    if (currentCompany) {
      switch (row.document) {
        case 'Tearsheet':
          dispatchGenerateTearSheet(currentCompany.id, null);
          return;
        case 'Primer':
          dispatchGeneratePrimer(currentCompany.id, null);
          return;
      }
    }

    return null;
  };

  const { clients: adminClients } = currentCompany || ({} as Bixby.Models.AdminCompany);

  const orderedClients: Bixby.Models.CompanyClient[] = Bixby.Utils.orderBy(adminClients || [], c => c.client_Name);

  const formattedDatePrimerCellRenderer = (params: ICellRendererParams) => {
    const date = formattedDate(params.data.primerUpdatedDate);
    return date ? date : '';
  };

  const formattedDateTearSheetCellRenderer = (params: ICellRendererParams) => {
    const date = formattedDate(params.data.tearSheetUpdatedDate);
    return date ? date : '';
  };

  const onDocumentGridReady = (params: any) => {
    setDocumentGridApi(params.api);
    setDocumentGridColumnApi(params.columnApi);
  };

  const onPrimerGridReady = (params: any) => {
    setPrimerGridApi(params.api);
    setPrimerGridColumnApi(params.columnApi);
  };

  const onTearSheetGridReady = (params: any) => {
    setTearSheetGridApi(params.api);
    setTearSheetGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = (params: any) => {
    if (params.column.colId === 'linkText' || params.column.colId === 'client_Id') {
      return [];
    }

    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const generateDocumentButton = (params: ICellRendererParams) => {
    return isSuperAdmin ? (
      <button
        className="bix_grid_button"
        type="button"
        disabled={isBixbyDocumentGenerateDisabled(params.data)}
        onClick={() => bixbyDocumentGenerate(params.data)}>
        Generate
      </button>
    ) : (
      ''
    );
  };

  const generateTearSheetButton = (params: ICellRendererParams) => {
    return isSuperAdmin ? (
      <button
        className="bix_grid_button"
        type="button"
        disabled={
          (
            (orderedClients.find(
              c => (c as Bixby.Models.CompanyClient).id == params.data.id
            ) as Bixby.Models.CompanyClient) || ({} as Bixby.Models.CompanyClient)
          ).tearSheetState === Bixby.Enums.DocumentState.Pending
        }
        onClick={() => (currentCompany ? dispatchGenerateTearSheet(currentCompany.id, params.data.client_Id) : null)}>
        Generate
      </button>
    ) : (
      ''
    );
  };

  const generatePrimerButton = (params: ICellRendererParams) => {
    return isSuperAdmin ? (
      <button
        className="bix_grid_button"
        type="button"
        disabled={
          (
            (orderedClients.find(
              c => (c as Bixby.Models.CompanyClient).id == params.data.id
            ) as Bixby.Models.CompanyClient) || ({} as Bixby.Models.CompanyClient)
          ).primerState === Bixby.Enums.DocumentState.Pending
        }
        onClick={() => (currentCompany ? dispatchGeneratePrimer(currentCompany.id, params.data.client_Id) : null)}>
        Generate
      </button>
    ) : (
      ''
    );
  };

  return currentCompany ? (
    <div className="admin-profile-section bottom">
      <h3>Bixby Documents</h3>
      <div className="company-documents-data-grid">
        <div className="grid_container">
          <div className="grid ag-theme-alpine">
            <AgGridReact
              getContextMenuItems={getContextMenuItems}
              defaultColDef={{
                flex: 1,
                minWidth: 100,
                sortable: true,
                resizable: true,
                lockPinned: true,
                cellClass: 'align-left',
              }}
              onGridReady={onDocumentGridReady}
              rowData={bixbyDocuments}
              rowHeight={35}
              headerHeight={35}
              components={{
                documentButtonCellRenderer: generateDocumentButton,
              }}
              columnDefs={[
                { field: 'document', headerName: 'Document Name', cellClass: 'align-left' },
                { field: 'state', headerName: 'State', cellClass: 'align-left' },
                { field: 'updatedDate', headerName: 'Updated Date', cellClass: 'align-left' },
                { field: 'updatedBy', headerName: 'Updated By', cellClass: 'align-left' },
                {
                  field: 'linkText',
                  headerName: 'Link',
                  cellClass: 'align-left',
                  cellRenderer: (params: ICellRendererParams) => {
                    const url = params.data['link'];
                    return currentCompany ? (
                      <a className="link_underline bix-link" target="_blank" href={url}>
                        {params.value}
                      </a>
                    ) : (
                      ''
                    );
                  },
                },
                {
                  headerName: 'Generate',
                  cellClass: 'align-center',
                  suppressMenu: true,
                  cellRenderer: 'documentButtonCellRenderer',
                },
              ]}></AgGridReact>
          </div>
        </div>
      </div>
      {isCurrentCompanyPublic ? (
        <p className="public-companies-message">Client documents not available for public companies.</p>
      ) : (
        <>
          <h3>Tearsheet</h3>
          {orderedClients && !Bixby.Utils.isNullOrEmpty(orderedClients) ? (
            <div className="company-tearsheet-primer-data-grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    getContextMenuItems={getContextMenuItems}
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100,
                      sortable: true,
                      resizable: true,
                      lockPinned: true,
                      cellClass: 'align-left',
                    }}
                    onGridReady={onTearSheetGridReady}
                    rowData={orderedClients}
                    rowHeight={35}
                    headerHeight={35}
                    components={{
                      tearSheetButtonCellRenderer: generateTearSheetButton,
                    }}
                    columnDefs={[
                      { field: 'client_Name', headerName: 'Client Name', cellClass: 'align-left' },
                      { field: 'tearSheetState', headerName: 'State', cellClass: 'align-left' },
                      {
                        field: 'tearSheetUpdatedDate',
                        headerName: 'Updated Date',
                        cellClass: 'align-left',
                        cellRenderer: formattedDateTearSheetCellRenderer,
                      },
                      { field: 'tearSheetUpdatedBy', headerName: 'Updated By', cellClass: 'align-left' },
                      {
                        field: 'client_Id',
                        headerName: 'Link',
                        cellClass: 'align-left',
                        cellRenderer: (params: ICellRendererParams) => {
                          const url =
                            currentCompany.documentLibraryWebUrl +
                            '/' +
                            Bixby.Constants.GENERATED_DOCUMENTS +
                            '/' +
                            params.value +
                            '/' +
                            Bixby.Constants.TEARSHEET;
                          return currentCompany ? (
                            <a className="link_underline bix-link" target="_blank" rel="noopener noreferrer" href={url}>
                              {Bixby.Constants.TEARSHEET}
                            </a>
                          ) : (
                            ''
                          );
                        },
                        suppressMenu: true,
                      },
                      {
                        headerName: 'Generate',
                        cellClass: 'align-center',
                        suppressMenu: true,
                        cellRenderer: 'tearSheetButtonCellRenderer',
                      },
                    ]}></AgGridReact>
                </div>
              </div>
            </div>
          ) : (
            <NoDataFound dataType="clients" />
          )}

          <h3>Primer</h3>
          {orderedClients && !Bixby.Utils.isNullOrEmpty(orderedClients) ? (
            <div className="company-tearsheet-primer-data-grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    getContextMenuItems={getContextMenuItems}
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100,
                      sortable: true,
                      resizable: true,
                      lockPinned: true,
                      cellClass: 'align-left',
                    }}
                    onGridReady={onPrimerGridReady}
                    rowData={orderedClients}
                    rowHeight={35}
                    headerHeight={35}
                    components={{
                      primerButtonCellRenderer: generatePrimerButton,
                    }}
                    columnDefs={[
                      { field: 'client_Name', headerName: 'Client Name', cellClass: 'align-left' },
                      { field: 'primerState', headerName: 'State', cellClass: 'align-left' },
                      {
                        field: 'primerUpdatedDate',
                        headerName: 'Updated Date',
                        cellClass: 'align-left',
                        cellRenderer: formattedDatePrimerCellRenderer,
                      },
                      { field: 'primerUpdatedBy', headerName: 'Updated By', cellClass: 'align-left' },
                      {
                        field: 'client_Id',
                        headerName: 'Link',
                        cellClass: 'align-left',
                        cellRenderer: (params: ICellRendererParams) => {
                          const url =
                            currentCompany.documentLibraryWebUrl +
                            '/' +
                            Bixby.Constants.GENERATED_DOCUMENTS +
                            '/' +
                            params.value +
                            '/' +
                            Bixby.Constants.PRIMER;
                          return currentCompany ? (
                            <a className="link_underline bix-link" target="_blank" rel="noopener noreferrer" href={url}>
                              {Bixby.Constants.PRIMER}
                            </a>
                          ) : (
                            ''
                          );
                        },
                        suppressMenu: true,
                      },
                      {
                        headerName: 'Generate',
                        cellClass: 'align-center',
                        suppressMenu: true,
                        cellRenderer: 'primerButtonCellRenderer',
                      },
                    ]}></AgGridReact>
                </div>
              </div>
            </div>
          ) : (
            <NoDataFound dataType="clients" />
          )}
        </>
      )}
    </div>
  ) : null;
};

const mapStateToProps = (state: AppState) => {
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  const { isCurrentCompanyPublic } = getIsCurrentCompanyPublicState(state);
  return {
    isCurrentCompanyPublic,
    currentCompany: state.companyState.currentCompany,
    isSuperAdmin,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGeneratePrimer: (companyId: string, clientId: string | null) => dispatch(generatePrimer(companyId, clientId)),
  dispatchGenerateTearSheet: (companyId: string, clientId: string | null) =>
    dispatch(generateTearSheet(companyId, clientId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyDocuments);
